import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import BabysitterDashboardHeader from "../BabysitterDashboardHeader/BabysitterDashboardHeader";
import StyledBgContainer from "../../../components/StyledBgContainer/StyledBgContainer";
import { useNavigate } from "react-router-dom";
import {
  getBabysitterDashboard,
  getStage,
  requestCheckin,
} from "../../../services/NetworkServices";
import {
  getToken,
  storeApprovalStatus,
  storeStageNumber,
} from "../../../utils/auth-token-utils/cookie";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/system";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
} from '@mui/material';
import './Dashboard.css';
import CheckinTick from '../../../assets/CheckinTickCircle.svg';
import CheckinFailure from '../../../assets/Close Circle.svg';
import CheckinPending from '../../../assets/checkInPending.svg';
import LeftArrow from '../../../assets/Left 1.svg';
import RightArrow from '../../../assets/Arrow narrow right.svg';
import { Pagination } from '@mui/material';
import DataUnavailable from '../../../components/DataUnavailable';
import StyledButton from '../../../components/Button/Button';
import DownloadCsv from '../../../assets/download-check-ins.svg';
import { formatTime } from '../../../utils';
import Popup from '../../../components/Inputs/Popup';
import PopupClose from '../../../assets/popupClose.svg';
import { AxiosError } from 'axios';
import alert_icon from '../../../assets/BabySitterDashboard/alert-icon.svg';

interface SubmissionData {
  checkIn_impose_button: string;
  time: string;
  authentication: string;
  city: string;
  country: string;
  state: string;
  sublocality_level_1: string;
  street_number: string;
  route: string;
  neighborhood: string;
}

interface Submission {
  submissionData: SubmissionData;
}

interface User {
  userName: string;
  profilePic: string;
  submissions: Submission[];
  userId: string;
}

interface Consumer {
  _id: string;
  users: User[];
  date: string;
}

interface AuthJwtPayload {
  user: {
    _id: string;
    email: string;
    role: string;
  };
}

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'transparent', // Black background for odd rows
    color: 'white',
  },
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(96, 111, 140, 0.18)',
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: '1px solid rgba(96, 111, 140, 0.15)',
  borderBottom: 'none',
  color: 'white',
}));

const BabysitterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [consumersList, setConsumersList] = useState<Consumer[]>([]);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showConfirmationPopup, setShowConfirmationPopup] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 5;
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [popupMessage, setPopupMessage] = useState<string>('');
  const [requestCheck, setRequestCheck] = useState<boolean>(false);
  const [requestCheckInId, setRequestCheckInId] = useState<string>('');

  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const fetchDashboardDetails = async (
    page: number,
    limit: number,
  ): Promise<void> => {
    try {
      if (process.env.NODE_ENV === "development") {
        const token = getToken();
        if (token) {
          try {
            const decoded = jwtDecode<AuthJwtPayload>(token);
            console.log(
              "[BabysitterDashboard] supervisor user id (JWT user._id):",
              decoded.user?._id ?? null,
            );
          } catch {
            console.log(
              "[BabysitterDashboard] supervisor user id: JWT decode failed",
            );
          }
        } else {
          console.log(
            "[BabysitterDashboard] supervisor user id: no token in storage",
          );
        }
      }

      const response = await getBabysitterDashboard(page, limit);
      const data = await response.data;

      if (process.env.NODE_ENV === "development") {
        console.log(
          "[BabysitterDashboard] babysitter-web-dashboard API response:",
          data,
        );
      }

      setTotalRecords(data.data.totalCount);
      const consumersWithId = data.data.results.map(
        (item: Omit<Consumer, 'id'>, index: number) => ({
          id: index + 1,
          ...item,
        }),
      );

      const filteredConsumersListByDate = consumersWithId.map(
        (consumer: Consumer) => ({
          date: consumer._id,
          users: consumer.users,
        }),
      );

      setConsumersList(filteredConsumersListByDate);
    } catch (error) {
      console.error('Error fetching dashboard details:', error);
    }
  };

  const handleRequestCheckins = async (value: string) => {
    const currentTime = new Date();
    const expiryTime = new Date(currentTime.getTime() + 60 * 60 * 1000);

    const checkInInitiatedTime = formatTime(currentTime);
    const checkInInitiatedExpiryTime = formatTime(expiryTime);
    const checkInInitiatedDate = currentTime.toISOString().split('T')[0];

    const requestData = {
      consumerId: value,
      checkIn_initiated_time: checkInInitiatedTime,
      checkIn_initiated_expiry_time: checkInInitiatedExpiryTime,
      checkIn_initiated_date: checkInInitiatedDate,
    };

    try {
      const response = await requestCheckin(requestData);
      const data = await response?.data;
      if (data) {
        setRequestCheck(true);
        setPopupMessage(data?.message);
        setShowConfirmationPopup(false);
        setShowPopup(true);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        setPopupMessage(error?.response?.data?.message);
        setShowConfirmationPopup(false);
        setShowPopup(true);
      }
    }
  };

  const handleDownloadCsv = (user: any) => {
    console.log('Download button clicked for:', user.userName);
    const submissions = user.submissions || [];

    const headers = [
      'User Name',
      'Check-in Initiated Time',
      'Check-in Expiry Time',
      'checkIn_initiated_date',
      'Authentication Status',
      'Location',
    ];

    const rows = submissions.map((submission: any) => {
      const { submissionData } = submission;

      const getLocation = (
        country: string,
        city: string,
        state: string,
        sublocality: string,
        streetNumber: string,
        route: string,
        neighborhood: string,
      ) => {
        const locationParts = [];
        if (country) locationParts.push(country);
        if (city) locationParts.push(city);
        if (state) locationParts.push(state);
        if (sublocality) locationParts.push(sublocality);
        if (streetNumber) locationParts.push(streetNumber);
        if (route) locationParts.push(route);
        if (neighborhood) locationParts.push(neighborhood);

        return locationParts.join(', ') || 'Location Not Available';
      };

      const location = getLocation(
        submissionData.country,
        submissionData.city,
        submissionData.state,
        submissionData.sublocality_level_1,
        submissionData.street_number,
        submissionData.route,
        submissionData.neighborhood,
      );

      return [
        user.userName,
        submissionData.checkIn_initiated_time,
        submissionData.checkIn_initiated_expiry_time,
        submissionData.checkIn_initiated_date,
        submissionData.authentication || 'Pending',
        `"${location}"`,
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row: any) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const userName = user.userName.replace(/\s+/g, '_');
    const fileName = `${userName}_submissions.csv`;

    if (link.download !== undefined) {
      console.log('Creating download link...');

      const url = URL.createObjectURL(blob);
      console.log('Blob URL: ', url);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } else {
      console.log('Download functionality not supported in this browser.');
    }
  };

  const renderCheckinIcon = (submission: {
    submissionData: { authentication: string };
  }) => {
    const authStatus = submission?.submissionData?.authentication;

    const isSuccess = authStatus === 'Success';
    const isPending = authStatus === 'Pending';

    return (
      <img
        src={isSuccess || isPending ? CheckinTick : CheckinFailure}
        alt={
          isSuccess
            ? 'checkin tick'
            : isPending
              ? 'checkin pending'
              : 'checkin failure tick'
        }
        style={{
          width: '25px',
          height: '25px',
          opacity: isPending ? '0.5' : '1',
        }}
      />
    );
  };

  const handlePopup = () => {
    setShowPopup(false);
    setShowConfirmationPopup(false);
    setRequestCheck(false);
  };

  const handleClickYes = () => {
    setShowConfirmationPopup(false);
    handleRequestCheckins(requestCheckInId);
  };

  const handleClickNo = () => {
    setShowConfirmationPopup(false);
  };

  useEffect(() => {
    const fetchStageDetails = async (): Promise<void> => {
      try {
        const response = await getStage();
        const data = await response.data;
        if (data) {
          const { stage, approvalStatus } = data.data;
          storeStageNumber(stage);
          if (approvalStatus) {
            storeApprovalStatus(approvalStatus);
          }
          if (approvalStatus && approvalStatus !== 'approved') {
            navigate('/babysitter/waitingForApproval');
            return;
          }
          if (stage === '1') {
            navigate('/supervisor/createProfile');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  useEffect(() => {
    fetchDashboardDetails(currentPage, itemsPerPage);
  }, [currentPage]);

  return (
    <StyledBgContainer>
      {showPopup && <div className="backdrop"> </div>}
      <Container maxWidth="lg">
        <BabysitterDashboardHeader />
        <TableContainer
          component={Paper}
          sx={{
            position: 'relative',

            borderRadius: '12px',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: 0, // Set all sides to 0, keeping it inside the box
              padding: '1px', // Thickness of the border
              borderRadius: 'inherit', // Ensure border-radius matches the container
              background:
                'linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%)', // Gradient border effect
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', // Mask to create the border effect
              maskComposite: 'exclude', // Use to ensure gradient acts as a border
              pointerEvents: 'none', // Allows clicks on the underlying content
            },
          }}
        >
          <Table sx={{ color: 'white' }}>
            <TableHead sx={{ color: 'white', backgroundColor: '#606F8C4D' }}>
              <TableRow
                sx={{
                  color: 'white !important',
                }}
              >
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Check-ins</StyledTableCell>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>Verified</StyledTableCell>
                <StyledTableCell
                  style={{ minWidth: '300px' }}
                ></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ color: 'white' }}>
              {consumersList && consumersList.length > 0 ? (
                consumersList.map((consumer) => (
                  <React.Fragment key={consumer.date}>
                      {consumer.users.map((user, index) => (
                        <StyledTableRow
                          key={user.userId}
                          sx={{
                            // backgroundColor:
                            //   index % 2 === 0
                            //     ? "black"
                            //     : "rgba(96, 111, 140, 0.08)", // Alternate styling
                            color: 'white !important',
                          }}
                        >
                          <StyledTableCell
                            sx={{ color: 'white' }}
                            onClick={() =>
                              navigate(
                                `/supervisor/participantDetails/${user.userId}`,
                              )
                            }
                          >
                            <Box
                              gap={1}
                              sx={{
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                              }}
                            >
                              <img
                                src={user.profilePic}
                                alt="profile pic"
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                }}
                              />
                              <p>{user?.userName}</p>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{
                              color: 'white',
                              width: { xs: '100px', md: '20%' },
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                paddingRight: '4px',
                                gap: '10px',
                              }}
                            >
                              {Array.from({ length: 3 }).map((_, index) => {
                                const submission = user?.submissions?.[index];
                                const authStatus =
                                  submission?.submissionData?.authentication
                                    ?.length >= 3
                                    ? submission.submissionData.authentication
                                    : 'Pending';
                                return (
                                  <img
                                    key={index}
                                    src={
                                      authStatus === 'Success'
                                        ? CheckinPending
                                        : authStatus === 'Pending'
                                          ? CheckinPending
                                          : CheckinFailure
                                    }
                                    alt={
                                      authStatus === 'Success'
                                        ? 'checkin tick'
                                        : authStatus === 'Pending'
                                          ? 'checkin pending'
                                          : 'checkin failure tick'
                                    }
                                    style={{
                                      width: '24px',
                                      height: '24px',
                                      opacity:
                                        authStatus === 'Pending' ? '0.5' : '1',
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: 'white' }}>
                            {user?.submissions[0]?.submissionData
                              ?.street_number &&
                              `${user.submissions[0].submissionData.street_number}, `}
                            {user?.submissions[0]?.submissionData?.route &&
                              `${user.submissions[0].submissionData.route}, `}
                            {user?.submissions[0]?.submissionData
                              ?.neighborhood &&
                              `${user.submissions[0].submissionData.neighborhood}, `}
                            {user?.submissions[0]?.submissionData
                              ?.sublocality_level_1 &&
                              `${user.submissions[0].submissionData.sublocality_level_1}, `}
                            {user?.submissions[0]?.submissionData?.city &&
                              `${user.submissions[0].submissionData.city}, `}
                            {user?.submissions[0]?.submissionData?.state &&
                              `${user.submissions[0].submissionData.state}, `}
                            {user?.submissions[0]?.submissionData?.country}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: 'white' }}>
                            {user?.submissions[0] &&
                              renderCheckinIcon(user.submissions[0])}
                          </StyledTableCell>
                          <StyledTableCell sx={{ color: 'white' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                gap: '20px',
                              }}
                            >
                              <StyledButton
                                onClick={() => {
                                  setRequestCheckInId(user?.userId);
                                  setShowConfirmationPopup(true);
                                }}
                              >
                                Request Check-in
                              </StyledButton>
                              {/* <button onClick={() => handleDownloadCsv(user)}> */}
                              <div onClick={() => handleDownloadCsv(user)}>
                                <img src={DownloadCsv} alt="download-csv" />
                              </div>
                              {/* </button> */}
                            </div>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}

                      {consumer.date && (
                        <StyledTableRow>
                          <StyledTableCell
                            colSpan={5}
                            sx={{ textAlign: 'center' }}
                          >
                            <Divider
                              variant="middle"
                              sx={{
                                '&::before, &::after': {
                                  borderColor: 'white',
                                  borderStyle: 'dashed',
                                  borderWidth: '1px',
                                  flex: 1,
                                },
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Chip
                                label={consumer.date}
                                sx={{
                                  borderRadius: '10px',
                                  backgroundColor: 'white',
                                  color: 'black',
                                  py: '20px',
                                }}
                              />
                            </Divider>
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                  </React.Fragment>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5}>
                    <DataUnavailable description="No participant record found" />
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalRecords > 5 && (
          <div className="table_pagination_container">
            <div
              className="pagination-btn-wrapper"
              onClick={handlePreviousPage}
            >
              <img src={LeftArrow} alt="Previous" /> Previous
            </div>
            <div>
              <Pagination
                count={Math.ceil(totalRecords / itemsPerPage)} // Use dynamic page count
                page={currentPage} // Controlled page value
                onChange={(e, value) => setCurrentPage(value)} // Update page on pagination change
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#FFFFFF66', // Default color for other pages
                  },
                  '& .Mui-selected': {
                    // Background for selected page
                    color: 'white', // Text color for selected page
                  },
                }}
              />
            </div>
            <div className="pagination-btn-wrapper" onClick={handleNextPage}>
              Next <img src={RightArrow} alt="Next" />
            </div>
          </div>
        )}

        {showConfirmationPopup && (
          <Popup
            icon={alert_icon}
            title=<Typography
              variant="h6"
              sx={{
                fontFamily: 'opensans',
                fontWeight: '700',
                lineHeight: '1.4',
              }}
            >
              <p>
                Are You Sure You <br /> Want to Request Check-in?
              </p>
            </Typography>
            buttonText="No"
            secondaryButtonText="Yes"
            onButtonClick={handleClickNo}
            onSecondaryButtonClick={handleClickYes}
          />
        )}

        {showPopup && (
          <Popup
            icon={!requestCheck && PopupClose}
            title=<Typography
              variant="h6"
              sx={{
                fontFamily: 'opensans',
                fontWeight: '700',
                lineHeight: '1.4',
              }}
            >
              {' '}
              <p style={{ padding: '0 5px' }}>{popupMessage}</p>
            </Typography>
            buttonText="Done"
            onButtonClick={handlePopup}
          />
        )}
      </Container>
    </StyledBgContainer>
  );
};

export default BabysitterDashboard;
